import React, { useEffect, useMemo, useState } from 'react';

const coverCache = new Map();

const getNormalizedIsbn = (rawIsbn) => {
  if (!rawIsbn) {
    return '';
  }

  const normalized = String(rawIsbn).toUpperCase().replace(/[^0-9X]/g, '');
  const match13 = normalized.match(/[0-9]{13}/);
  if (match13) {
    return match13[0];
  }

  const match10 = normalized.match(/[0-9]{9}[0-9X]/);
  if (match10) {
    return match10[0];
  }

  return '';
};

const getInternetCoverUrl = (rawIsbn, sizeCode) => {
  const isbn = getNormalizedIsbn(rawIsbn);
  if (!isbn) {
    return '';
  }

  return `https://covers.openlibrary.org/b/isbn/${isbn}-${sizeCode}.jpg?default=false`;
};

const getPlaceholderSvg = (width, height, label) => {
  const safeLabel = String(label || 'No Cover');
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'><rect width='100%' height='100%' fill='#e2e8f0'/><rect x='1' y='1' width='${Math.max(width - 2, 1)}' height='${Math.max(height - 2, 1)}' fill='none' stroke='#94a3b8'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='Arial,sans-serif' font-size='12' fill='#334155'>${safeLabel}</text></svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

function CoverImage({
  biblioId,
  isbn,
  alt,
  className,
  width,
  height,
  sizeCode = 'M',
  placeholderLabel = 'No Cover',
}) {
  const cacheKey = `${biblioId}-${sizeCode}-${width}x${height}`;
  const initialSrc = coverCache.get(cacheKey) || `/api/books/${biblioId}/cover`;
  const [src, setSrc] = useState(initialSrc);
  const placeholderSrc = useMemo(
    () => getPlaceholderSvg(width, height, placeholderLabel),
    [width, height, placeholderLabel],
  );

  // If this same component instance is reused for a different book (e.g. a card-stack UI that
  // swaps data without remounting), `src` must follow `cacheKey` — otherwise it stays frozen on
  // whichever image loaded first.
  useEffect(() => {
    setSrc(coverCache.get(cacheKey) || `/api/books/${biblioId}/cover`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const handleError = () => {
    const internetCover = getInternetCoverUrl(isbn, sizeCode);

    if (internetCover && src !== internetCover) {
      setSrc(internetCover);
      return;
    }

    if (src !== placeholderSrc) {
      coverCache.set(cacheKey, placeholderSrc);
      setSrc(placeholderSrc);
    }
  };

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      referrerPolicy="no-referrer"
      loading="lazy"
      onError={handleError}
    />
  );
}

export default CoverImage;
